/**
 * 🔔 Wastage Reason Event Service
 * Real-time event broadcasting for wastage reason changes
 * 
 * Usage:
 *   import wastageReasonEventService from '../services/wastageReasonEventService';
 * 
 *   // Subscribe to events
 *   const unsubscribe = wastageReasonEventService.subscribe((event) => {
 *     if (event.type === 'REASON_ADDED') console.log('New reason:', event.data);
 *     if (event.type === 'REASON_DELETED') console.log('Deleted:', event.data);
 *   });
 * 
 *   // Broadcast events
 *   wastageReasonEventService.reasonAdded({ reason_id: 1, reason_label: 'Spillage' });
 *   wastageReasonEventService.reasonDeleted(1);
 * 
 *   // Cleanup
 *   unsubscribe();
 */

class WastageReasonEventService {
  constructor() {
    this.listeners = [];
  }

  /**
   * Subscribe to wastage reason events
   * @param {Function} callback - Function called when events occur
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('WastageReasonEventService: callback must be a function');
      return () => {};
    }
    
    this.listeners.push(callback);
    
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Broadcast event to all subscribers
   * @param {Object} event - Event object with type and data
   */
  broadcast(event) {
    if (!event || !event.type) {
      console.error('WastageReasonEventService: Invalid event format', event);
      return;
    }
    
    
    this.listeners.forEach((listener, index) => {
      try {
        listener(event);
      } catch (error) {
        console.error(`WastageReasonEventService: Error in listener ${index}:`, error);
      }
    });
  }

  /**
   * Notify that a new wastage reason was added
   * @param {Object} reason - The added reason object
   */
  reasonAdded(reason) {
    if (!reason || !reason.reason_label) {
      console.error('WastageReasonEventService: Invalid reason for added event');
      return;
    }
    
    this.broadcast({
      type: 'REASON_ADDED',
      data: reason,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Notify that a wastage reason was deleted
   * @param {Number|String} reasonId - The ID of the deleted reason
   */
  reasonDeleted(reasonId) {
    if (!reasonId) {
      console.error('WastageReasonEventService: Invalid reason ID for deleted event');
      return;
    }
    
    this.broadcast({
      type: 'REASON_DELETED',
      data: reasonId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Notify that wastage reasons should be refreshed
   */
  reasonsRefreshed() {
    this.broadcast({
      type: 'REASONS_REFRESHED',
      timestamp: new Date().toISOString()
    });
  }
}

// Export singleton instance
const wastageReasonEventService = new WastageReasonEventService();

export default wastageReasonEventService;
